import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CartContext = createContext();

// Mirror of the backend discount calculation (for live display only;
// the backend re-validates and recomputes authoritatively at checkout).
const expandUnitPrices = (items = []) => {
  const prices = [];
  items.forEach((item) => {
    const price = Number(item.price) || 0;
    const qty = Number(item.quantity) || 0;
    for (let i = 0; i < qty; i += 1) {
      prices.push(price);
    }
  });
  return prices;
};

const computeDiscount = (coupon, subtotal, items = []) => {
  if (!coupon) return 0;

  if (coupon.discountType === 'bxgy') {
    const buyQty = Number(coupon.buyQuantity || 0);
    const getQty = Number(coupon.getQuantity || 0);
    if (buyQty < 1 || getQty < 1) return 0;

    const unitPrices = expandUnitPrices(items);
    const groupSize = buyQty + getQty;
    if (unitPrices.length < groupSize) return 0;

    const freeCount = Math.floor(unitPrices.length / groupSize) * getQty;
    if (freeCount <= 0) return 0;

    unitPrices.sort((a, b) => a - b);
    const discount = unitPrices.slice(0, freeCount).reduce((sum, p) => sum + p, 0);
    return Math.round(Math.max(0, discount) * 100) / 100;
  }

  let discount = 0;
  if (coupon.discountType === 'percentage') {
    discount = subtotal * (Number(coupon.discountValue || 0) / 100);
    const cap = Number(coupon.maxDiscountAmount || 0);
    if (cap > 0) discount = Math.min(discount, cap);
  } else {
    discount = Number(coupon.discountValue || 0);
  }
  discount = Math.min(discount, subtotal);
  return Math.round(Math.max(0, discount) * 100) / 100;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
    const savedCoupon = localStorage.getItem('appliedCoupon');
    if (savedCoupon) {
      try {
        const parsed = JSON.parse(savedCoupon);
        setAppliedCoupon(parsed);
        setCouponCode(parsed.code || '');
      } catch (e) {
        localStorage.removeItem('appliedCoupon');
      }
    }
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Recompute the discount whenever the cart or applied coupon changes.
  // If the coupon no longer qualifies (e.g. cart dropped below the minimum),
  // it is automatically removed.
  useEffect(() => {
    const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

    if (!appliedCoupon) {
      setDiscount(0);
      return;
    }

    const minOrder = Number(appliedCoupon.minOrderValue || 0);
    if (minOrder > 0 && subtotal < minOrder) {
      setAppliedCoupon(null);
      setCouponCode('');
      setDiscount(0);
      localStorage.removeItem('appliedCoupon');
      return;
    }

    if (appliedCoupon.discountType === 'bxgy') {
      const buyQty = Number(appliedCoupon.buyQuantity || 0);
      const getQty = Number(appliedCoupon.getQuantity || 0);
      const needed = buyQty + getQty;
      const unitCount = expandUnitPrices(cartItems).length;
      if (needed > 0 && unitCount < needed) {
        setAppliedCoupon(null);
        setCouponCode('');
        setDiscount(0);
        localStorage.removeItem('appliedCoupon');
        return;
      }
    }

    setDiscount(computeDiscount(appliedCoupon, subtotal, cartItems));
  }, [cartItems, appliedCoupon]);

  const addToCart = (product, size, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(
        item => item.id === product.id && item.size === size
      );

      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id && item.size === size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevItems, { ...product, size, quantity, cartId: Date.now() }];
      }
    });
  };

  const removeFromCart = (cartId) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
  };

  const updateQuantity = (cartId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(cartId);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.cartId === cartId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setCouponCode('');
    setAppliedCoupon(null);
    setDiscount(0);
    localStorage.removeItem('appliedCoupon');
  };

  const getCartTotal = () => {
    const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    return Math.max(0, subtotal - discount);
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const applyCoupon = useCallback(async (code) => {
    const normalized = (code || '').trim().toUpperCase();
    if (!normalized) {
      return { success: false, message: 'Please enter a coupon code' };
    }

    const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    const items = cartItems.map((item) => ({
      price: item.price,
      quantity: item.quantity,
    }));

    try {
      const response = await api.publicAPI.validateCoupon(normalized, subtotal, items);
      if (response.data.success) {
        const coupon = response.data.coupon;
        setAppliedCoupon(coupon);
        setCouponCode(coupon.code);
        setDiscount(response.data.discount);
        localStorage.setItem('appliedCoupon', JSON.stringify(coupon));
        return { success: true, discount: response.data.discount };
      }
      return { success: false, message: response.data.error || 'Invalid coupon code' };
    } catch (error) {
      const message = error.response?.data?.error || 'Invalid coupon code';
      return { success: false, message };
    }
  }, [cartItems]);

  const removeCoupon = useCallback(() => {
    setCouponCode('');
    setAppliedCoupon(null);
    setDiscount(0);
    localStorage.removeItem('appliedCoupon');
  }, []);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getSubtotal,
    getTotalItems,
    applyCoupon,
    removeCoupon,
    couponCode,
    discount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 